import { useState } from "react";
import {
  Typography, Table, Button, Modal, message, Spin, Card, Row, Col,
  Popconfirm, Form, Tooltip, Input, Checkbox
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";

import DeviceModal from "./DeviceModal";
import useBasicDevices from "../hooks/useBasicDevices";
import useDeviceActions from "../hooks/useDeviceActions";
import useTableSearchPagination from '../hooks/useTableSearchPagination';

const { Title, Paragraph } = Typography;
const { confirm: ConfirmModal } = Modal;

export default function DevicesView() {
  const [form] = Form.useForm();
  const { devices, loading, error } = useBasicDevices();
  const { saveDevice, deleteDevice, reload } = useDeviceActions();

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const deviceTypes = Array.from(
    new Map(
      devices.map(d => [d.entityType?.id, d.entityType])
    ).values()
  ).filter(Boolean);

  const openCreateModal = () => {
    setMode("create");
    setSelectedDevice(null);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setMode("edit");
    setSelectedDevice(record);
    setModalOpen(true);
  };

  const handleSave = async (values, form) => {
    setSaving(true);
    const result = await saveDevice(mode, selectedDevice?.id, values);

    if (result.success) {
      message.success(result.message);
      form.resetFields();
      setModalOpen(false);
      reload();
    } else {
      message.error(result.message);
    }

    setSaving(false);
  };

  const handleDelete = (record) => {
    ConfirmModal({
      title: "Are you sure you want to delete this device?",
      icon: <ExclamationCircleOutlined />,
      content: `${record.name} (${record.serialNumber}) will be permanently removed.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const result = await deleteDevice(record.id);
        if (result.success) {
          message.success(result.message);
          reload();
        } else {
          message.error(result.message);
        }
      },
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Serial Number", dataIndex: "serialNumber", key: "serialNumber" },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Latitude",
      dataIndex: "latitude",
      key: "latitude",
      render: lat => lat != null ? lat.toFixed(6) : "-"
    },
    {
      title: "Longitude",
      dataIndex: "longitude",
      key: "longitude",
      render: lng => lng != null ? lng.toFixed(6) : "-"
    },
    {
      title: "Anemometer",
      dataIndex: "hasAnemometer",
      key: "hasAnemometer",
      render: val => val ? "Yes" : "No"
    },
    {
      title: "Type",
      dataIndex: ["entityType", "name"],
      key: "entityType.id"
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isVirtual = record.entityType?.id === 3;
        return (
          <>
            <Tooltip title="Edit">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => openEditModal(record)}
                style={{ marginRight: 8 }}
              />
            </Tooltip>

            {isVirtual && (
              <Popconfirm
                title="Delete device?"
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Delete">
                  <Button icon={<DeleteOutlined />} size="small" danger />
                </Tooltip>
              </Popconfirm>
            )}
          </>
        );
      }
    }
  ];

  const filterDevice = (d, searchText) => {
    const q = searchText.toLowerCase();

    const matchesSearch =
      (d.name || "").toLowerCase().includes(q) ||
      (d.serialNumber || "").toLowerCase().includes(q) ||
      (d.description || "").toLowerCase().includes(q) ||
      (d.entityType?.name || "").toLowerCase().includes(q) ||
      (d.hasAnemometer ? "yes" : "no").includes(q);

    const matchesType =
      selectedTypes.length === 0 ||
      selectedTypes.includes(d.entityType?.id);

    return matchesSearch && matchesType;
  };

  const {
    searchText,
    onSearchChange,
    filteredData: filteredDevices,
    pagination
  } = useTableSearchPagination({
    data: devices,
    filterFn: filterDevice,
    extraDeps: [selectedTypes]
  });

  return (
    <div style={{ padding: 24 }}>
      <Row style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Devices</Title>
        </Col>
      </Row>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Row align="middle" gutter={16}>
            <Col>
              <Input
                placeholder="Search devices..."
                value={searchText}
                onChange={e => onSearchChange(e.target.value)}
                allowClear
                style={{ width: 300 }}
              />
            </Col>

            <Col>
              <Checkbox.Group
                value={selectedTypes}
                onChange={(checkedValues) => setSelectedTypes(checkedValues)}
              >
                {deviceTypes.map(type => (
                  <Checkbox key={type.id} value={type.id}>
                    {type.name}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Col>
          </Row>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            style={{ backgroundColor: "#0075C9" }}
          >
            Create Device
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Paragraph type="danger" style={{ marginTop: 40 }}>
          Error loading devices.
        </Paragraph>
      ) : !filteredDevices.length ? (
        <Paragraph style={{ marginTop: 40 }}>No devices found.</Paragraph>
      ) : (
        <Card>
          <Table
            rowKey="id"
            dataSource={filteredDevices}
            columns={columns}
            size="middle"
            pagination={pagination}
          />
        </Card>
      )}

      <DeviceModal
        open={modalOpen}
        mode={mode}
        form={form}
        selectedDevice={selectedDevice}
        saving={saving}
        onCancel={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}
